<script setup>
// Aba "Proposta": monta a proposta comercial a partir do template do produto
// (hoje ChatSuite; outros produtos = novos templates), gera o LINK público
// somente-leitura (/pp/:token, com botão de PDF) e envia na conversa.
import { ref, computed, onMounted, watch } from 'vue';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import ProposalsAPI from 'dashboard/api/proposals';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const props = defineProps({
  conversation: { type: Object, required: true },
});

const store = useStore();

const L = {
  template: 'Produto da proposta',
  client: 'Cliente',
  title: 'Título da proposta',
  discount: 'Aplicar desconto de',
  plan: 'Plano',
  planHint: 'Escolha a faixa. O preço vem do catálogo — não é editável aqui.',
  quotas: 'Cotas negociadas',
  quotasHint:
    'Ajuste os limites combinados com este cliente. Eles aparecem na proposta no lugar dos números padrão.',
  addons: 'Recursos opcionais',
  addonsHint: 'Marque o que entra nesta proposta — o total recalcula sozinho.',
  validity: 'Validade',
  conditions: 'Condições',
  contact: 'Contato comercial',
  monthlyTotal: 'Investimento mensal total',
  setupTitle: 'Implantação e setup',
  setupValue: 'Valor',
  setupDiscount: 'Desconto',
  setupTotal: 'Implantação (pagamento único)',
  once: ' uma vez',
  plusSetup: '+ implantação',
  pct: '%',
  brl: 'R$',
  generate: 'Gerar proposta (link)',
  history: 'Propostas geradas',
  copy: 'Copiar link',
  open: 'Abrir',
  send: 'Enviar na conversa',
  del: 'Excluir',
  sent: 'enviada',
  draft: 'rascunho',
  empty: 'Nenhuma proposta gerada ainda.',
  linkCopied: 'Link copiado!',
  sentOk: 'Proposta enviada na conversa.',
  created: 'Proposta gerada!',
  createdCopied: 'Proposta gerada — link copiado!',
  lastGenerated: 'Proposta gerada — link pronto:',
  deleteOk: 'Proposta excluída.',
  error: 'Algo deu errado. Tente de novo.',
  notIncluded: 'Não incluído',
  each: 'cada',
  perMonth: '/mês',
  messageText:
    'Preparei a sua proposta 🙂 Você pode ver os detalhes e baixar em PDF por aqui:',
};

const templates = ref([]);
const proposals = ref([]);
const lastCreated = ref(null);
const isLoading = ref(true);
const isSaving = ref(false);

const selectedKey = ref('chatsuite');
const template = computed(() =>
  templates.value.find(t => t.key === selectedKey.value)
);

const selections = ref({});
// Faixa do produto (FlexCalls: Starter/Business/Personalizado). Produto de
// plano único (ChatSuite) não manda `plans` e nada disto aparece.
const planKey = ref(null);
const quotas = ref({});
const plans = computed(() => template.value?.plans || []);
const plan = computed(
  () => plans.value.find(p => p.key === planKey.value) || null
);
const planQuotas = computed(() => plan.value?.quotas || []);
const discountEnabled = ref(false);
const discountPct = ref(10);
const discountKind = ref('percent');

// Implantação: pagamento único, valor negociado caso a caso e desconto próprio
// — o desconto do rodapé é da mensalidade e não encosta aqui.
const setupEnabled = ref(false);
const setupPrice = ref(0);
const setupDiscountKind = ref('percent');
const setupDiscountValue = ref(0);
const clientName = ref('');
const title = ref('');
const validity = ref('7 dias');
const conditions = ref('Valores em reais. Pagamento conforme contrato.');
const commercialContact = ref('');

const fmt = n =>
  Number(n || 0).toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
const money = n => `R$ ${fmt(n)}`;

const subtotal = computed(() => {
  if (!template.value) return 0;
  // Plano cobrado por uso (R$0,89/minuto) NÃO é mensalidade: somá-lo aqui
  // faria a tela anunciar "R$ 0,89/mês", igual ao que a proposta anunciaria.
  let sum = plan.value
    ? Number(plan.value.is_usage ? 0 : plan.value.price)
    : Number(template.value.base_price);
  template.value.addons.forEach(a => {
    const sel = selections.value[a.id];
    if (a.type === 'stepper' && sel > 0) sum += a.price * sel;
    if (a.type === 'toggle' && sel === true) sum += a.price;
  });
  return sum;
});
// Percentual entre 0 e 100; abatimento em reais nunca passa da própria linha.
const discountOf = (base, kind, value) => {
  if (base <= 0) return 0;
  const raw =
    kind === 'amount'
      ? Number(value || 0)
      : (base * Math.min(100, Math.max(0, Number(value || 0)))) / 100;
  return Math.min(Math.max(raw, 0), base);
};

const total = computed(
  () =>
    subtotal.value -
    (discountEnabled.value
      ? discountOf(subtotal.value, discountKind.value, discountPct.value)
      : 0)
);

const setupSubtotal = computed(() =>
  setupEnabled.value ? Math.max(0, Number(setupPrice.value || 0)) : 0
);
const setupTotal = computed(
  () =>
    setupSubtotal.value -
    discountOf(
      setupSubtotal.value,
      setupDiscountKind.value,
      setupDiscountValue.value
    )
);

// Cada produto tem o seu valor de implantação; ao trocar de template o campo
// volta para o padrão daquele produto.
// Trocar de produto reseta a faixa e as cotas: manter a escolha do produto
// anterior geraria proposta com cota que aquele plano nem tem.
watch(template, tpl => {
  const list = tpl?.plans || [];
  planKey.value = list.length ? tpl.default_plan || list[0].key : null;
});
// Trocar de faixa recarrega as cotas com o padrão daquela faixa.
watch(planQuotas, list => {
  quotas.value = Object.fromEntries(list.map(q => [q.id, q.default]));
});
watch(template, tpl => {
  setupPrice.value = Number(tpl?.setup?.default_price || 0);
});

const step = (addon, delta) => {
  const current = Number(selections.value[addon.id] || 0);
  selections.value[addon.id] = Math.min(
    addon.max || 99,
    Math.max(0, current + delta)
  );
};
const toggle = addon => {
  selections.value[addon.id] = !selections.value[addon.id];
};

const loadAll = async () => {
  try {
    const [tpl, list] = await Promise.all([
      ProposalsAPI.templates(),
      ProposalsAPI.list(props.conversation.id),
    ]);
    templates.value = tpl.data || [];
    proposals.value = list.data || [];
    if (templates.value.length) selectedKey.value = templates.value[0].key;
  } catch {
    templates.value = [];
  } finally {
    isLoading.value = false;
  }
};

const createProposal = async () => {
  if (!template.value) return;
  isSaving.value = true;
  try {
    const payload = {
      conversation_id: props.conversation.id,
      template_key: selectedKey.value,
      // Faixa e cotas negociadas. A TARIFA não vai no payload de propósito: o
      // backend a lê do registry e ignora preço vindo da tela.
      plan_key: planKey.value || undefined,
      quotas: planQuotas.value.length ? quotas.value : undefined,
      client_name: clientName.value,
      title: title.value,
      validity: validity.value,
      conditions: conditions.value,
      commercial_contact: commercialContact.value,
      discount: {
        enabled: discountEnabled.value,
        kind: discountKind.value,
        value: discountPct.value,
      },
      setup: {
        enabled: setupEnabled.value,
        price: setupPrice.value,
        discount_kind: setupDiscountKind.value,
        discount_value: setupDiscountValue.value,
      },
      // Só o que foi REALMENTE escolhido: stepper com qty>0, toggle marcado.
      selections: template.value.addons
        .filter(a =>
          a.type === 'stepper'
            ? Number(selections.value[a.id] || 0) > 0
            : selections.value[a.id] === true
        )
        .map(a => ({
          id: a.id,
          qty: a.type === 'stepper' ? Number(selections.value[a.id]) : 1,
        })),
    };
    const { data } = await ProposalsAPI.createProposal(payload);
    proposals.value = [data, ...proposals.value];
    lastCreated.value = data;
    copyTextToClipboard(data.public_url);
    useAlert(L.createdCopied);
  } catch {
    useAlert(L.error);
  } finally {
    isSaving.value = false;
  }
};

const copyLink = async proposal => {
  await copyTextToClipboard(proposal.public_url);
  useAlert(L.linkCopied);
};

const sendInConversation = async proposal => {
  try {
    await store.dispatch('createPendingMessageAndSend', {
      conversationId: props.conversation.id,
      message: `${L.messageText}\n${proposal.public_url}`,
    });
    await ProposalsAPI.markSent(proposal.id);
    proposal.status = 'sent';
    useAlert(L.sentOk);
  } catch {
    useAlert(L.error);
  }
};

const removeProposal = async proposal => {
  try {
    await ProposalsAPI.deleteProposal(proposal.id);
    proposals.value = proposals.value.filter(p => p.id !== proposal.id);
    useAlert(L.deleteOk);
  } catch {
    useAlert(L.error);
  }
};

const formatDate = iso =>
  new Date(iso).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });

onMounted(() => {
  clientName.value = props.conversation?.meta?.sender?.name || '';
  const user = store.getters.getCurrentUser;
  commercialContact.value = user?.email || '';
  loadAll();
});
</script>

<template>
  <div class="p-4">
    <div v-if="isLoading" class="flex justify-center py-10">
      <Spinner />
    </div>

    <div v-else-if="template" class="flex flex-col gap-4">
      <!-- Identificação -->
      <div class="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div>
          <label class="mb-1 block text-xs font-medium text-n-slate-11">{{
            L.template
          }}</label>
          <select
            v-model="selectedKey"
            class="reset-base w-full rounded-lg border border-n-weak bg-n-background px-2.5 py-1.5 text-sm text-n-slate-12 outline-none focus:border-n-brand"
          >
            <option v-for="t in templates" :key="t.key" :value="t.key">
              {{ t.name }}
            </option>
          </select>
        </div>
        <div>
          <label class="mb-1 block text-xs font-medium text-n-slate-11">{{
            L.client
          }}</label>
          <input
            v-model="clientName"
            type="text"
            class="reset-base w-full rounded-lg border border-n-weak bg-n-background px-2.5 py-1.5 text-sm text-n-slate-12 outline-none focus:border-n-brand"
          />
        </div>
      </div>
      <div>
        <label class="mb-1 block text-xs font-medium text-n-slate-11">{{
          L.title
        }}</label>
        <input
          v-model="title"
          type="text"
          :placeholder="template.title_default"
          class="reset-base w-full rounded-lg border border-n-weak bg-n-background px-2.5 py-1.5 text-sm text-n-slate-12 outline-none focus:border-n-brand"
        />
      </div>

      <!-- Faixas do produto (só aparece em produto com mais de um plano) -->
      <div v-if="plans.length">
        <p class="text-sm font-medium text-n-slate-12">{{ L.plan }}</p>
        <p class="mb-2 text-xs text-n-slate-11">{{ L.planHint }}</p>
        <div class="grid grid-cols-1 gap-2 sm:grid-cols-3">
          <button
            v-for="p in plans"
            :key="p.key"
            type="button"
            class="rounded-lg border px-3 py-2 text-left"
            :class="
              planKey === p.key
                ? 'border-n-brand bg-n-alpha-2'
                : 'border-n-weak'
            "
            @click="planKey = p.key"
          >
            <span class="block text-sm font-medium text-n-slate-12">
              {{ p.name }}
            </span>
            <span class="block text-sm text-n-slate-11">
              {{ money(p.price) }}{{ p.unit }}
            </span>
            <span class="block mt-1 text-xs text-n-slate-10">
              {{ p.tagline }}
            </span>
          </button>
        </div>
      </div>

      <!-- Cotas negociadas (só no plano personalizado) -->
      <div v-if="planQuotas.length">
        <p class="text-sm font-medium text-n-slate-12">{{ L.quotas }}</p>
        <p class="mb-2 text-xs text-n-slate-11">{{ L.quotasHint }}</p>
        <div class="grid grid-cols-1 gap-2 sm:grid-cols-2">
          <div
            v-for="q in planQuotas"
            :key="q.id"
            class="flex items-center justify-between rounded-lg border border-n-weak px-3 py-2"
          >
            <span class="text-sm text-n-slate-12">{{ q.label }}</span>
            <input
              v-model.number="quotas[q.id]"
              type="number"
              min="0"
              class="reset-base w-24 rounded-lg border border-n-weak bg-n-background px-2 py-1 text-sm text-right text-n-slate-12 outline-none focus:border-n-brand"
            />
          </div>
        </div>
      </div>

      <!-- Adicionais -->
      <div v-if="template.addons && template.addons.length">
        <p class="text-sm font-medium text-n-slate-12">{{ L.addons }}</p>
        <p class="mb-2 text-xs text-n-slate-10">{{ L.addonsHint }}</p>
        <div class="grid grid-cols-1 gap-2 xl:grid-cols-2">
          <div
            v-for="addon in template.addons"
            :key="addon.id"
            class="flex items-start gap-2.5 rounded-xl border p-3 transition-colors"
            :class="
              (addon.type === 'stepper' && selections[addon.id] > 0) ||
              selections[addon.id] === true
                ? 'border-n-teal-8 bg-n-teal-2'
                : 'border-n-weak bg-n-solid-1'
            "
          >
            <span class="text-lg">{{ addon.icon }}</span>
            <div class="min-w-0 flex-1">
              <div class="flex items-start justify-between gap-2">
                <span class="text-sm font-medium text-n-slate-12">{{
                  addon.name
                }}</span>
                <span
                  class="whitespace-nowrap text-xs font-semibold text-n-slate-11"
                >
                  {{ money(addon.price) }}{{ L.perMonth
                  }}{{ addon.type === 'stepper' ? ` ${L.each}` : '' }}
                </span>
              </div>
              <p class="mt-0.5 text-xs text-n-slate-10">{{ addon.desc }}</p>
              <div class="mt-2 flex items-center gap-2">
                <template v-if="addon.type === 'stepper'">
                  <div
                    class="flex items-center overflow-hidden rounded-lg border border-n-weak"
                  >
                    <button
                      type="button"
                      class="flex size-6 items-center justify-center text-n-slate-11 hover:bg-n-alpha-2"
                      @click="step(addon, -1)"
                    >
                      <span class="i-lucide-minus size-3.5" />
                    </button>
                    <span
                      class="w-7 text-center text-xs font-semibold text-n-slate-12"
                      >{{ selections[addon.id] || 0 }}</span
                    >
                    <button
                      type="button"
                      class="flex size-6 items-center justify-center text-n-slate-11 hover:bg-n-alpha-2"
                      @click="step(addon, 1)"
                    >
                      <span class="i-lucide-plus size-3.5" />
                    </button>
                  </div>
                  <span class="text-[11px] text-n-slate-10">
                    {{
                      selections[addon.id] > 0
                        ? `R$ ${fmt(addon.price * selections[addon.id])}${L.perMonth}`
                        : L.notIncluded
                    }}
                  </span>
                </template>
                <label v-else class="flex cursor-pointer items-center gap-2">
                  <input
                    type="checkbox"
                    class="size-4 accent-[#12B886]"
                    :checked="selections[addon.id] === true"
                    @change="toggle(addon)"
                  />
                </label>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Implantação: pagamento único, valor e desconto próprios -->
      <div
        v-if="template.setup"
        class="flex items-start gap-2.5 rounded-xl border p-3 transition-colors"
        :class="
          setupEnabled
            ? 'border-n-teal-8 bg-n-teal-2'
            : 'border-n-weak bg-n-solid-1'
        "
      >
        <span class="text-lg">🚀</span>
        <div class="min-w-0 flex-1">
          <div class="flex items-start justify-between gap-2">
            <label class="flex cursor-pointer items-center gap-2">
              <input
                v-model="setupEnabled"
                type="checkbox"
                class="size-4 accent-[#12B886]"
              />
              <span class="text-sm font-medium text-n-slate-12">
                {{ template.setup.label || L.setupTitle }}
              </span>
            </label>
            <span
              v-if="setupEnabled"
              class="whitespace-nowrap text-xs font-semibold text-n-teal-11"
            >
              {{ money(setupTotal) }}{{ L.once }}
            </span>
          </div>
          <p class="mt-0.5 text-xs text-n-slate-10">
            {{ template.setup.desc }}
          </p>
          <div
            v-if="setupEnabled"
            class="mt-2 flex flex-wrap items-center gap-2"
          >
            <span class="text-xs text-n-slate-11">{{ L.setupValue }}</span>
            <input
              v-model.number="setupPrice"
              type="number"
              min="0"
              step="50"
              class="reset-base w-24 rounded-lg border border-n-weak bg-n-background px-2 py-1 text-sm text-n-slate-12"
            />
            <span class="text-xs text-n-slate-11">{{ L.setupDiscount }}</span>
            <input
              v-model.number="setupDiscountValue"
              type="number"
              min="0"
              class="reset-base w-20 rounded-lg border border-n-weak bg-n-background px-2 py-1 text-sm text-n-slate-12"
            />
            <select
              v-model="setupDiscountKind"
              class="reset-base rounded-lg border border-n-weak bg-n-background px-2 py-1 text-sm text-n-slate-12"
            >
              <option value="percent">{{ L.pct }}</option>
              <option value="amount">{{ L.brl }}</option>
            </select>
          </div>
        </div>
      </div>

      <!-- Desconto + rodapé -->
      <div class="flex flex-wrap items-center gap-3">
        <label
          class="flex cursor-pointer items-center gap-2 text-sm text-n-slate-11"
        >
          <input
            v-model="discountEnabled"
            type="checkbox"
            class="size-4 accent-[#12B886]"
          />
          {{ L.discount }}
        </label>
        <input
          v-model.number="discountPct"
          type="number"
          min="0"
          class="reset-base w-20 rounded-lg border border-n-weak bg-n-background px-2 py-1 text-center text-sm text-n-slate-12"
          :disabled="!discountEnabled"
        />
        <select
          v-model="discountKind"
          class="reset-base rounded-lg border border-n-weak bg-n-background px-2 py-1 text-sm text-n-slate-12"
          :disabled="!discountEnabled"
        >
          <option value="percent">{{ L.pct }}</option>
          <option value="amount">{{ L.brl }}</option>
        </select>
      </div>
      <div class="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <div>
          <label class="mb-1 block text-xs font-medium text-n-slate-11">{{
            L.validity
          }}</label>
          <input
            v-model="validity"
            type="text"
            class="reset-base w-full rounded-lg border border-n-weak bg-n-background px-2.5 py-1.5 text-sm text-n-slate-12"
          />
        </div>
        <div>
          <label class="mb-1 block text-xs font-medium text-n-slate-11">{{
            L.conditions
          }}</label>
          <input
            v-model="conditions"
            type="text"
            class="reset-base w-full rounded-lg border border-n-weak bg-n-background px-2.5 py-1.5 text-sm text-n-slate-12"
          />
        </div>
        <div>
          <label class="mb-1 block text-xs font-medium text-n-slate-11">{{
            L.contact
          }}</label>
          <input
            v-model="commercialContact"
            type="text"
            class="reset-base w-full rounded-lg border border-n-weak bg-n-background px-2.5 py-1.5 text-sm text-n-slate-12"
          />
        </div>
      </div>

      <!-- Total + gerar -->
      <div
        class="flex items-center justify-between rounded-xl border border-n-weak bg-n-solid-1 p-4"
      >
        <div>
          <p class="text-xs text-n-slate-10">{{ L.monthlyTotal }}</p>
          <p class="text-2xl font-bold text-n-teal-11">
            {{ money(total)
            }}<span class="text-sm text-n-slate-10">{{ L.perMonth }}</span>
          </p>
          <p v-if="setupTotal > 0" class="mt-0.5 text-xs text-n-slate-11">
            {{ L.setupTotal }}:
            <span class="font-semibold text-n-slate-12">{{
              money(setupTotal)
            }}</span>
          </p>
        </div>
        <Button
          :label="L.generate"
          icon="i-lucide-file-text"
          :is-loading="isSaving"
          @click="createProposal"
        />
      </div>

      <!-- Última gerada: link na cara, com ações -->
      <div
        v-if="lastCreated"
        class="rounded-xl border border-n-teal-8 bg-n-teal-2 p-4"
      >
        <p class="mb-1 text-xs font-medium text-n-teal-11">
          {{ L.lastGenerated }}
        </p>
        <a
          :href="lastCreated.public_url"
          target="_blank"
          rel="noopener noreferrer"
          class="break-all text-sm font-medium text-n-blue-11 hover:underline"
        >
          {{ lastCreated.public_url }}
        </a>
        <div class="mt-3 flex flex-wrap items-center gap-2">
          <Button
            size="sm"
            icon="i-lucide-copy"
            :label="L.copy"
            @click="copyLink(lastCreated)"
          />
          <Button
            size="sm"
            variant="faded"
            icon="i-lucide-send"
            :label="L.send"
            @click="sendInConversation(lastCreated)"
          />
          <a
            :href="lastCreated.public_url"
            target="_blank"
            rel="noopener noreferrer"
          >
            <Button
              size="sm"
              variant="faded"
              color="slate"
              icon="i-lucide-external-link"
              :label="L.open"
            />
          </a>
        </div>
      </div>

      <!-- Histórico -->
      <div>
        <p class="mb-2 text-sm font-medium text-n-slate-12">{{ L.history }}</p>
        <p v-if="!proposals.length" class="text-xs text-n-slate-10">
          {{ L.empty }}
        </p>
        <div
          v-for="proposal in proposals"
          :key="proposal.id"
          class="mb-2 flex flex-wrap items-center justify-between gap-2 rounded-xl border border-n-weak bg-n-solid-1 px-3 py-2.5"
        >
          <div class="min-w-0">
            <p class="text-sm font-medium text-n-slate-12">
              {{ money(proposal.total) }}{{ L.perMonth }}
              <span
                class="ml-1 rounded-full px-2 py-0.5 text-[11px] font-medium"
                :class="
                  proposal.status === 'sent'
                    ? 'bg-n-teal-3 text-n-teal-11'
                    : 'bg-n-alpha-2 text-n-slate-11'
                "
                >{{ proposal.status === 'sent' ? L.sent : L.draft }}</span
              >
            </p>
            <p class="text-[11px] text-n-slate-10">
              <template v-if="proposal.setup_total > 0">
                {{ L.plusSetup }} {{ money(proposal.setup_total) }} ·
              </template>
              {{ proposal.client_name }} · {{ formatDate(proposal.created_at) }}
            </p>
          </div>
          <div class="flex items-center gap-1">
            <Button
              v-tooltip="L.copy"
              variant="ghost"
              size="sm"
              icon="i-lucide-copy"
              @click="copyLink(proposal)"
            />
            <a
              :href="proposal.public_url"
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button
                v-tooltip="L.open"
                variant="ghost"
                size="sm"
                icon="i-lucide-external-link"
              />
            </a>
            <Button
              v-tooltip="L.send"
              variant="ghost"
              size="sm"
              icon="i-lucide-send"
              @click="sendInConversation(proposal)"
            />
            <Button
              v-tooltip="L.del"
              variant="ghost"
              size="sm"
              color="ruby"
              icon="i-lucide-trash-2"
              @click="removeProposal(proposal)"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
